import * as React from 'react';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { Viewer, LoadError, PdfJs, Plugin } from '@react-pdf-viewer/core';
import { ModalWindow } from '../ModalWindow';
import { ErrorMessage } from '../ErrorMessage';
import { PageLoading } from '../Loading';
import styled from 'styled-components';
import { Card } from '../Card';
import { FC } from 'react';
import getServerRequestHeader from 'policyUI/utils/getServerRequestHeader';
import ReactDOM from 'react-dom';
import { printPlugin } from '@react-pdf-viewer/print';

type ePreviewStyle = 'modal' | 'card';

export interface IPDFPreviewProps {
  file: string | File | Uint8Array | null;
  httpHeader?: Record<string, string>;
  onClose: () => void;
  previewStyle?: ePreviewStyle;
  printOnLoad?: boolean;
  onDocumentLoad?: () => void;
}

interface IPDFViewerProps {
  file: string | Uint8Array | File;
  httpHeader?: Record<string, string>;
  onDocumentLoad?: () => void;
  plugins?: Plugin[];
}

const PreviewWrapper: FC<{
  previewStyle?: ePreviewStyle;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}> = ({ previewStyle, onClose, children, title }) => (
  <>
    {previewStyle === 'card' ? (
      <Card>{children}</Card>
    ) : (
      <ModalWindow width={'80%'} onClose={onClose} title={title}>
        {children}
      </ModalWindow>
    )}
  </>
);

const isFile = (file: string | File | Uint8Array): file is File => (file as File).type !== undefined;

export const PDFViewer = ({ file, httpHeader, onDocumentLoad, plugins = [] }: IPDFViewerProps) => {
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  return (
    <Viewer
      fileUrl={isFile(file) ? URL.createObjectURL(file) : file}
      plugins={typeof file === 'string' ? [defaultLayoutPluginInstance, ...plugins] : plugins}
      httpHeaders={httpHeader ?? getServerRequestHeader(typeof file === 'string' ? file : '')}
      transformGetDocumentParams={(options: PdfJs.GetDocumentParams) => ({ ...options })}
      renderLoader={() => <>{ReactDOM.createPortal(<PageLoading isLoading={true} />, document.body)}</>}
      renderError={(error: LoadError) => <ErrorMessage message={error.message} />}
      onDocumentLoad={onDocumentLoad}
    />
  );
};

export const PDFPreview: React.FC<IPDFPreviewProps> = ({
  file,
  onClose,
  httpHeader,
  previewStyle,
  printOnLoad = false,
  onDocumentLoad = () => {},
}) => {
  const printPluginInstance = printPlugin();
  const { print } = printPluginInstance;
  const onLoad = React.useCallback(() => {
    if (printOnLoad) {
      print();
    }
    onDocumentLoad?.();
  }, [onDocumentLoad, printOnLoad, print]);
  return (
    <>
      {!!file && (
        <PreviewWrapper previewStyle={previewStyle} onClose={onClose}>
          <StyledPdfWrapper>
            <PDFViewer file={file} httpHeader={httpHeader} onDocumentLoad={onLoad} plugins={[printPluginInstance]} />
          </StyledPdfWrapper>
        </PreviewWrapper>
      )}
    </>
  );
};

export const StyledPdfWrapper = styled.div`
  height: 750px;
  width: 100%;
  overflow: auto;

  button {
    min-width: 0rem;
  }
`;
